// Re-validate the cookie session for an open tab and, if it now belongs to a
// DIFFERENT person, start the tab over.
//
// All main-app tabs share one session cookie. Signing in as someone else in
// another tab silently changes who THIS tab's requests run as; the identity sync
// (initializeAuthThunk → syncSessionIdentity) adopts the new person, but every
// list already on screen still holds the previous person's — often another
// company's — data. Acting on it then fails confusingly (PROD 2026-10-22: an
// Approve went out as the new account → "Change request not found"). A reload is
// the only reset that covers page-local state as well as caches.

export interface PrincipalRecheckDeps {
  /** The user id the tab is currently showing. */
  currentUserId: () => string | null | undefined;
  /** Re-validate against the server session (may change currentUserId). */
  revalidate: () => Promise<unknown>;
  /** Persist the adopted identity first, so the reload does not loop. */
  flush: () => Promise<unknown>;
  reload: () => void;
}

export type PrincipalRecheckResult = 'same' | 'reloaded';

export async function recheckSessionPrincipal(deps: PrincipalRecheckDeps): Promise<PrincipalRecheckResult> {
  const before = deps.currentUserId() ?? null;
  await deps.revalidate();
  const after = deps.currentUserId() ?? null;
  // Logout (after=null) is handled by the auth flow itself; a tab that showed
  // nobody (before=null) has no stale data to discard.
  if (!before || !after || before === after) return 'same';
  try {
    await deps.flush();
  } catch {
    /* storage unavailable — reload anyway; the session is the authority */
  }
  deps.reload();
  return 'reloaded';
}
